import React, { useEffect, useState } from "react";
import { Card, Segmented, Spin } from "antd";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import {
  getProfitLossBalanceApi,
} from "../../../apiservice/dashboard";
import {
  currentDateStartTime,
  currentDateEndTime,
} from "../../../lib/functions";
import dayjs from "dayjs";
import { fetchDashboardLastSevenMonthSalesAndPurchase } from "../../../store/commonThunks/dashboard";
import { useDispatch, useSelector } from "react-redux";
import { selectDashboardLastSevenMonthSalesAndPurchase } from "../../../store/commonSelectors/lookupsSelectors";
import ChartTooltip from "./ChartTooltip";
import { SERIES, INK, axisTick, shortAmount } from "../dashboardTheme";

// One entry per tab: which data set it plots and its two series.
const TABS = [
  {
    key: "1",
    label: "Debit / Credit",
    sub: "Monthly credit and debit totals",
    series: [
      { dataKey: "credit", name: "Credit", color: SERIES.aqua },
      { dataKey: "debit", name: "Debit", color: SERIES.red },
    ],
  },
  {
    key: "2",
    label: "Sales / Purchase",
    sub: "Monthly sales against purchases",
    series: [
      { dataKey: "sales", name: "Sales", color: SERIES.blue },
      { dataKey: "purchase", name: "Purchase", color: SERIES.orange },
    ],
  },
  {
    key: "3",
    label: "Profit / Loss",
    sub: "Monthly gross and net profit",
    series: [
      { dataKey: "grossProfit", name: "Gross Profit", color: SERIES.violet },
      { dataKey: "netProfit", name: "Net Profit", color: SERIES.aqua },
    ],
  },
];

const DashboardCharts = ({ currency = "AED", isMobile = false }) => {
  const dispatch = useDispatch();

  const salesPurchase = useSelector(
    selectDashboardLastSevenMonthSalesAndPurchase
  );

  const [activeTab, setActiveTab] = useState("1");

  const [profitLossData, setProfitLossData] = useState([]);
  const [debitCreditData, setDebitCreditData] = useState([]);
  const [salesPurchaseData, setSalesPurchaseData] = useState([]);

  const [loadedTabs, setLoadedTabs] = useState({
    profitLoss: false,
    debitCredit: false,
  });

  const [loadingTab, setLoadingTab] = useState(null);

  const getLast7MonthsDates = () => {
    const dates = [];
    for (let i = 6; i >= 0; i--) {
      const start = dayjs().subtract(i, "month").startOf("month");
      const end = dayjs().subtract(i, "month").endOf("month");
      dates.push({
        label: start.format("MMMM"),
        fromDate: currentDateStartTime(start),
        toDate: currentDateEndTime(end),
      });
    }
    return dates;
  };

  // ----------------------------
  // Initial load: Sales / Purchase only
  // ----------------------------
  useEffect(() => {
    dispatch(fetchDashboardLastSevenMonthSalesAndPurchase());
  }, [dispatch]);

  useEffect(() => {
    setSalesPurchaseData(salesPurchase);
  }, [salesPurchase]);

  useEffect(() => {
    if (activeTab === "1" && !loadedTabs.profitLoss) {
      loadProfitAndDebitCredit();
    }
  }, []);

  // ----------------------------
  // Shared API: Profit/Loss + Debit/Credit
  // ----------------------------
  const loadProfitAndDebitCredit = async () => {
    setLoadingTab("pl");

    try {
      const months = getLast7MonthsDates();

      const reports = await Promise.all(
        months.map((m) =>
          getProfitLossBalanceApi({
            fromDate: m.fromDate,
            toDate: m.toDate,
            page: "dashboard",
          }).then((res) => ({
            month: m.label,
            netProfit: res.data?.netProfit || 0,
            grossProfit: res.data?.grossProfitAmount || 0,
            creditGraph: res.data?.creditGraph || 0,
            debitGraph: res.data?.debitGraph || 0,
          }))
        )
      );

      setProfitLossData(
        reports.map((r) => ({
          month: r.month,
          grossProfit: Number(r.grossProfit),
          netProfit: Number(r.netProfit),
        }))
      );

      setDebitCreditData(
        reports.map((r) => ({
          month: r.month,
          credit: r.creditGraph,
          debit: r.debitGraph,
        }))
      );

      setLoadedTabs({
        profitLoss: true,
        debitCredit: true,
      });
    } catch (err) {
      console.error("Failed to load profit / debit-credit charts", err);
    } finally {
      // Never leave the chart stuck on a spinner if a month fails to load.
      setLoadingTab(null);
    }
  };

  // ----------------------------
  // Tab change handler (lazy load)
  // ----------------------------
  const handleTabChange = async (key) => {
    setActiveTab(key);

    if ((key === "1" || key === "3") && !loadedTabs.profitLoss) {
      if (loadingTab) return; // prevent double click
      await loadProfitAndDebitCredit();
    }
  };

  const tab = TABS.find((t) => t.key === activeTab);
  const data =
    activeTab === "1" ? debitCreditData : activeTab === "2" ? salesPurchaseData : profitLossData;
  const waiting = loadingTab && activeTab !== "2";

  return (
    <Card className="erp-card" variant="borderless">
      <div className="erp-card-head">
        <div>
          <div className="erp-card-title">Monthly Overview</div>
          <div className="erp-card-sub">{tab.sub} · last 7 months</div>
        </div>
        <Segmented
          className="erp-seg"
          size="small"
          value={activeTab}
          onChange={handleTabChange}
          options={TABS.map((t) => ({
            value: t.key,
            label: isMobile ? t.label.replace(" / ", "/") : t.label,
          }))}
        />
      </div>

      <div className="erp-legend">
        {tab.series.map((s) => (
          <span key={s.dataKey}>
            <i style={{ background: s.color }} />
            {s.name}
          </span>
        ))}
      </div>

      {waiting ? (
        <div className="erp-loading" style={{ height: isMobile ? 200 : 240 }}>
          <Spin />
        </div>
      ) : (
        <div className="erp-chart">
          <ResponsiveContainer width="100%" height={isMobile ? 200 : 240}>
            <BarChart data={data || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={2}>
              <CartesianGrid stroke={INK.grid} strokeOpacity={0.7} vertical={false} />
              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={{ stroke: INK.axis }}
                tick={axisTick}
                tickFormatter={(m) => String(m).slice(0, 3)}
              />
              <YAxis
                width={isMobile ? 44 : 56}
                tickLine={false}
                axisLine={false}
                tick={axisTick}
                tickFormatter={shortAmount}
              />
              <Tooltip
                content={<ChartTooltip currency={currency} />}
                cursor={{ fill: "#f1f4f9" }}
              />
              {tab.series.map((s) => (
                <Bar
                  key={s.dataKey}
                  dataKey={s.dataKey}
                  name={s.name}
                  fill={s.color}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={22}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
};

export default DashboardCharts;
